import { Button, toast, Typography } from "@suraasa/placebo-ui"
import SuraasaAppIcon from "assets/logos/suraasa-app-icon.png"
import { AuthManager } from "features/Authentication/AuthManager"
import { context } from "global/Context/context"
import { GrowthSpaceImage } from "growthspace/DefaultLogo"
import { LogOut } from "iconoir-react"
import { useContext } from "react"
import { IS_LOCALHOST } from "utils/constants"
import { buildUserName, generateAuthCode } from "utils/helpers"

import AndroidInline from "../assets/android.webp"
import AndroidMobile from "../assets/android-mobile.webp"
import AppStoreBadge from "../assets/app-store.webp"
import AppleInline from "../assets/apple.webp"
import GooglePlayBadge from "../assets/google-play.webp"
import IOSMobile from "../assets/ios-mobile.webp"
import Mobile1 from "../assets/mobile-1.webp"
import Mobile2 from "../assets/mobile-2.webp"
import Mobile3 from "../assets/mobile-3.webp"
import Mobile4 from "../assets/mobile-4.webp"
import Mobile5 from "../assets/mobile-5.webp"
import Mobile6 from "../assets/mobile-6.webp"
import QrCodeImg from "../assets/qrcode.webp"
import OsSpecificImage from "./OsSpecificImage"

const APP_STORE_URL =
  "https://apps.apple.com/app/suraasa-for-teachers/id1531878030"
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.suraasa.suraasa"

const SuraasaSureDownloadPrompt = () => {
  const { growthspaces, currentGrowthspace, switchGrowthspace } =
    useContext(context)

  const org = currentGrowthspace?.organization
  const otherSpaces = growthspaces.filter(g => !g.isCurrent)

  const user = AuthManager.getPlatformAuth()?.user

  const showDebugTools = sessionStorage.getItem("debug") === "1" || IS_LOCALHOST

  const copyAutoLoginLink = async () => {
    const code = await generateAuthCode()
    if (!code) {
      toast.error("Failed to generate auth code")
      return
    }
    const url = `suraasalearn://suraasa.com/observe?org_id=${
      org?.id ?? ""
    }&auth_code=${code}`
    await navigator.clipboard.writeText(url)
    toast.success("Auto-login link copied")
  }

  return (
    <div className="relative grid h-screen w-full grid-cols-1 overflow-hidden bg-white md:grid-cols-[45%_55%]">
      {/* ── Left panel ── */}
      <div className="z-1 flex size-full flex-col overflow-y-auto bg-white px-6 py-10 shadow-[2px_0_25px_0_rgba(0,0,0,0.08)] sm:px-10 md:px-12">
        <div className="m-auto w-full max-w-[420px]">
          {/* Org context badge */}
          {org && (
            <div className="mb-2 flex items-center gap-1 self-start">
              <GrowthSpaceImage
                growthspace={currentGrowthspace}
                className="size-8"
              />
              <Typography variant="title3" className="text-onSurface-600">
                {org.name}
              </Typography>
            </div>
          )}
          <Typography variant="title1" className="mb-4 text-onSurface-900">
            Download the Suraasa
            <span>
              <img
                src={SuraasaAppIcon}
                alt="Suraasa"
                className="mx-1 mb-1 inline size-[25px] align-middle shadow-sm sm:size-[35px]"
              />
            </span>
            App
          </Typography>

          {/* Sub-copy */}
          <Typography variant="largeBody" className="mb-6 text-onSurface-500">
            {org ? (
              <>
                Your Suraasa Sure license from{" "}
                <span className="text-strong">{org.name}</span> is only
                accessible through the Suraasa mobile app.
              </>
            ) : (
              "Your Suraasa Sure license is only accessible through the Suraasa mobile app."
            )}
          </Typography>

          {/* QR code — wide layouts only */}
          <div className="mb-6 hidden items-center gap-1 md:flex">
            <div className="shrink-0 rounded-2xl border border-onSurface-100 bg-white p-1 shadow-sm">
              <img src={QrCodeImg} alt="Scan to download" className="size-16" />
            </div>
            <Typography
              variant="title3"
              className="font-medium text-onSurface-800"
            >
              Scan QR code to download the Suraasa app on iOS{" "}
              <img
                src={AppleInline}
                alt="iOS"
                className="mb-1 inline h-2.5 align-middle"
              />{" "}
              or Android{" "}
              <img
                src={AndroidInline}
                alt="Android"
                className="mb-0.5 inline h-2.5 align-middle"
              />
              .
            </Typography>
          </div>

          {/* Store badges — narrow layouts only */}
          <div className="mb-6 flex w-full flex-col items-center gap-1.5 md:hidden">
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <img
                src={AppStoreBadge}
                alt="Download on the App Store"
                className="h-auto w-full max-w-[200px]"
              />
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <img
                src={GooglePlayBadge}
                alt="Get it on Google Play"
                className="h-auto w-full max-w-[200px]"
              />
            </a>
          </div>

          {/* App preview screenshots — visible on mobile/tablet only, inside the scrollable panel */}
          <div className="mx-auto mb-6 md:hidden">
            <OsSpecificImage
              appleImage={IOSMobile}
              androidImage={AndroidMobile}
            />
          </div>

          {/* Space switcher */}
          {otherSpaces.length > 0 && (
            <>
              <div className="mb-2 flex items-center gap-3">
                <hr className="flex-1 border-onSurface-100" />
                <Typography variant="smallBody" className="text-onSurface-600">
                  or continue in another space
                </Typography>
                <hr className="flex-1 border-onSurface-100" />
              </div>
              <div className="flex flex-col gap-1">
                {otherSpaces.map(item => (
                  <button
                    key={item.organization?.id ?? item.userUuid}
                    onClick={() =>
                      switchGrowthspace(
                        item.organization?.id.toString() ?? "personal"
                      )
                    }
                    className="flex w-full items-center gap-2 rounded-xl border border-onSurface-200 bg-surface-100 p-2 text-start hover:bg-surface-200"
                  >
                    <GrowthSpaceImage
                      growthspace={item}
                      className="size-[30px]"
                    />
                    <div className="flex flex-col">
                      <Typography
                        variant="smallBody"
                        className="text-onSurface-800"
                      >
                        {item.organization?.name ??
                          (user ? buildUserName(user) : "Personal Growthspace")}
                      </Typography>
                      <Typography
                        variant="smallBody"
                        className="text-onSurface-500"
                      >
                        {item.organization
                          ? "School Growthspace"
                          : "Personal Growthspace"}
                      </Typography>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Debug-only auto-login link */}
          {showDebugTools && (
            <div className="mt-4 flex justify-center">
              <Button
                color="black"
                variant="text"
                size="sm"
                onClick={copyAutoLoginLink}
              >
                Copy auto-login link for Suraasa App
              </Button>
            </div>
          )}

          {/* Sign out */}
          <div className="my-4 flex items-center gap-3">
            <hr className="flex-1 border-onSurface-100" />
            <Typography variant="smallBody" className="text-onSurface-600">
              or
            </Typography>
            <hr className="flex-1 border-onSurface-100" />
          </div>
          <div className="flex justify-center">
            <Button
              variant="text"
              size="sm"
              className="text-onSurface-500"
              endAdornment={<LogOut className="stroke-[2px]" />}
              onClick={() => AuthManager.redirectToLogout()}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      {/* ── Right panel (desktop only) ── */}
      <div className="hidden h-full overflow-hidden pb-6 md:block lg:px-7">
        <div className="relative grid h-full grid-cols-3 gap-4 [--duration:200s] [--gap:1rem]">
          {[
            [Mobile1, Mobile2],
            [Mobile3, Mobile4],
            [Mobile5, Mobile6],
          ].map((column, colIndex) => (
            <div
              key={colIndex}
              className="flex flex-col gap-[var(--gap)] overflow-hidden"
            >
              {[0, 1, 2, 3].map(repeatIdx => (
                <div
                  key={repeatIdx}
                  className={`flex shrink-0 animate-marquee-vertical flex-col justify-around gap-[var(--gap)] ${
                    colIndex % 2 === 0 ? "[animation-direction:reverse]" : ""
                  }`}
                >
                  {column.map((img, imgIndex) => (
                    <img
                      key={imgIndex}
                      src={img}
                      alt=""
                      className="w-full rounded-2xl"
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SuraasaSureDownloadPrompt
